import { Card, CardContent } from "@/components/ui/card";

const getScoreColor = (score, par) => {
  if (!score) return "text-gray-400";
  const diff = score - par;
  if (diff <= -2) return "text-[#D4A373] font-bold";
  if (diff === -1) return "text-[#15803D] font-semibold";
  if (diff === 0) return "text-[#0F172A]";
  if (diff === 1) return "text-[#B91C1C]";
  return "text-[#B91C1C] font-semibold";
};

export function ScoresTab({ round, HOLES, outPar, inPar, coursePar }) {
  const totalGross = {};
  round?.players?.forEach(player => {
    totalGross[player.player_id] = player.scores?.reduce((sum, s) => sum + (s.gross_score || 0), 0) || 0;
  });

  return (
    <Card className="card-default overflow-x-auto">
      <CardContent className="p-4">
        <h3 className="text-lg font-bold text-[#0F172A] mb-4">Scorecard Individual</h3>
        
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 px-2 font-semibold text-[#64748B] sticky left-0 bg-white">Hoyo</th>
                {[...Array(9)].map((_, i) => (
                  <th key={`h${i + 1}`} className="text-center py-2 px-2 font-semibold text-[#64748B] min-w-[40px]">{i + 1}</th>
                ))}
                <th className="text-center py-2 px-2 font-semibold text-[#1B4D3E] bg-gray-50">OUT</th>
                {[...Array(9)].map((_, i) => (
                  <th key={`h${i + 10}`} className="text-center py-2 px-2 font-semibold text-[#64748B] min-w-[40px]">{i + 10}</th>
                ))}
                <th className="text-center py-2 px-2 font-semibold text-[#1B4D3E] bg-gray-50">IN</th>
                <th className="text-center py-2 px-2 font-semibold text-[#1B4D3E] bg-[#1B4D3E]/10">TOT</th>
              </tr>
              <tr className="border-b border-gray-200 bg-gray-50">
                <td className="py-1 px-2 text-xs text-[#64748B] sticky left-0 bg-gray-50">Par</td>
                {HOLES.slice(0, 9).map((h) => (
                  <td key={`par2-${h.hole}`} className="text-center py-1 px-2 text-xs text-[#64748B]">{h.par}</td>
                ))}
                <td className="text-center py-1 px-2 text-xs font-semibold text-[#64748B] bg-gray-100">{outPar}</td>
                {HOLES.slice(9).map((h) => (
                  <td key={`par2-${h.hole}`} className="text-center py-1 px-2 text-xs text-[#64748B]">{h.par}</td>
                ))}
                <td className="text-center py-1 px-2 text-xs font-semibold text-[#64748B] bg-gray-100">{inPar}</td>
                <td className="text-center py-1 px-2 text-xs font-semibold text-[#64748B] bg-[#1B4D3E]/10">{coursePar}</td>
              </tr>
            </thead>
            <tbody>
              {round?.players?.map((player, pIdx) => {
                const outScore = player.scores?.filter(s => s.hole_number <= 9).reduce((sum, s) => sum + s.gross_score, 0) || 0;
                const inScore = player.scores?.filter(s => s.hole_number > 9).reduce((sum, s) => sum + s.gross_score, 0) || 0;
                const totalScore = outScore + inScore;
                
                return (
                  <tr key={player.player_id} className={`border-b border-gray-100 ${pIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                    <td className={`py-2 px-2 font-medium sticky left-0 ${pIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                      <div className="flex items-center gap-1.5">
                        <span className={`w-2 h-2 rounded-full shrink-0 ${player.is_base ? 'bg-[#1B4D3E]' : 'bg-[#D4A373]'}`}></span>
                        <span className="truncate max-w-[60px] sm:max-w-[100px] text-xs sm:text-sm" title={player.name}>{player.name.split(' ')[0]}</span>
                      </div>
                    </td>
                    {[...Array(9)].map((_, i) => {
                      const holeScore = player.scores?.find(s => s.hole_number === i + 1);
                      const par = HOLES[i].par;
                      return (
                        <td key={`s-${player.player_id}-h${i+1}`} className={`text-center py-2 px-2 ${getScoreColor(holeScore?.gross_score, par)}`}>
                          {holeScore?.gross_score || '-'}
                        </td>
                      );
                    })}
                    <td className="text-center py-2 px-2 font-bold text-[#1B4D3E] bg-gray-100">{outScore || '-'}</td>
                    {[...Array(9)].map((_, i) => {
                      const holeScore = player.scores?.find(s => s.hole_number === i + 10);
                      const par = HOLES[i + 9].par;
                      return (
                        <td key={`s-${player.player_id}-h${i+10}`} className={`text-center py-2 px-2 ${getScoreColor(holeScore?.gross_score, par)}`}>
                          {holeScore?.gross_score || '-'}
                        </td>
                      );
                    })}
                    <td className="text-center py-2 px-2 font-bold text-[#1B4D3E] bg-gray-100">{inScore || '-'}</td>
                    <td className="text-center py-2 px-2 font-bold text-[#1B4D3E] bg-[#1B4D3E]/10">{totalScore || '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Player Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mt-6">
          {round?.players?.map((player) => (
            <div 
              key={player.player_id}
              className={`p-3 rounded-lg border ${player.is_base ? 'bg-[#1B4D3E]/5 border-[#1B4D3E]/20' : 'bg-[#D4A373]/10 border-[#D4A373]/20'}`}
            >
              <p className="font-semibold text-sm text-[#0F172A] truncate" title={player.name}>{player.name}</p>
              <p className="text-xs text-[#64748B]">Hcp: {player.handicap}</p>
              <p className="text-xl font-bold mt-1 text-[#0F172A]">{totalGross[player.player_id] || 0}</p>
              <p className="text-xs text-[#64748B]">
                {(totalGross[player.player_id] || 0) - coursePar >= 0 ? '+' : ''}{(totalGross[player.player_id] || 0) - coursePar} vs par
              </p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
